import { generateIdFromArn, getIdInfoFromArn, getScopeForArn, Scope } from 'src/components/utils/arnUtil';
import { isValidWorkspace } from 'src/commons/validationUtils';
import {
  DZ_REDSHIFT_DATASOURCE_ID,
  LAKE_FORMATION_DATASOURCE_ID,
  REDSHIFT_DATASOURCE_ID,
  S3_TABLE_DATASOURCE_ID,
} from 'src/commons/constants';
import { TEMPLATE_RESOURCE_NONE } from 'src/components/templates/common';

export const Page = {
  /*
        NOTE: the order of the paths should be noted here, as they are checked
        in this order. For example, connections/new comes before connections/:id
        so that 'new' is not taken as the ID of a connection and the wrong page
        rendered. Breadcrumbs.tsx, breadcrumbx.tsx, and Routes.tsx should follow
        this order too.
      */

  HOME: '/',
  ONBOARDING: '/onboarding',
  CREATE_NEW_GROUP: '/groups/new',

  BROWSE_CATALOGS: '/catalogs',

  BROWSE_DATABASES: '/databases',

  BROWSE_DATASETS: '/datasets',

  METADATA_FORMS: '/metadataForms',
  VIEW_METADATA_FORM: '/metadataForms/:id/:formOwnerId',
  CREATE_METADATA_FORM: '/metadataForms/create',
  EDIT_METADATA_FORM: '/metadataForms/edit',

  CLASSIFICATION: '/classification',

  BUSINESS_GLOSSARIES: '/glossaries',
  VIEW_BUSINESS_GLOSSARY: '/glossaries/:id/:glossaryOwnerId',
  CREATE_BUSINESS_GLOSSARY: '/glossaries/create',
  EDIT_BUSINESS_GLOSSARY: '/glossaries/edit',

  GOVERNANCE_TAGS: '/governance-tags',
  VIEW_GOVERNANCE_TAG: '/governance-tags/:id/:glossaryOwnerId',
  CREATE_GOVERNANCE_TAG: '/governance-tags/create',
  EDIT_GOVERNANCE_TAG: '/governance-tags/edit',

  PUBLISH: '/publish',

  PUBLISHER_TABLES: '/publisher/tables',
  CREATE_PUBLISHER_TABLE: '/publisher/tables/new',

  MY_DATASETS: '/mydatasets',
  CREATE_LAKE_FORMATION_PERMISSIONS: '/mydatasets/lakeformation/new',
  DATASETSHARE_DETAILS: '/mydatasets/:id',

  REQUESTS: '/requests',
  REQUEST_DETAILS: '/requests/:id',

  EVENT_SUBSCRIPTIONS: '/eventsubscriptions',
  EVENT_SUBSCRIPTIONS_DETAILS: '/eventsubscriptions/:id',

  RESOURCEGROUPS: '/resourcegroups',
  CREATE_RESOURCEGROUP: '/resourcegroups/new',
  UPDATE_RESOURCEGROUP: '/resourcegroups/edit/:id',
  CREATE_RG_TEMPLATE: '/resourcegroups/templates/new',
  UPDATE_RG_TEMPLATE: '/resourcegroups/templates/edit/:id',
  RG_TEMPLATE_DETAILS: '/resourcegroups/templates/:id',
  RESOURCEGROUP_DETAILS: '/resourcegroups/:id',
  RESOURCE_DETAILS: '/resourcegroups/:rgid/:id',

  BOOTSTRAPACTIONS: '/bootstrapactions',
  CREATE_BOOTSTRAPACTION: '/bootstrapactions/new',
  UPDATE_BOOTSTRAPACTION: '/bootstrapactions/edit/:id',
  BOOTSTRAPACTION_DETAILS: '/bootstrapactions/:id',

  CHECKOUT: '/checkout',

  NOTIFICATIONS: '/notifications',
  CREATE_NOTIFICATION: '/notifications/new',
  NOTIFICATION_DETAILS: '/notifications/:NotificationId',
  NOTIFICATION_BATCH_DETAILS: '/notification-batch/:NotificationBatchId',

  NOTIFICATION_TEMPLATES: '/notificationtemplates',
  CREATE_NOTIFICATION_TEMPLATE: '/notificationtemplates/create',
  UPDATE_NOTIFICATION_TEMPLATE: '/notificationtemplates/:NotificationTemplateId/update',
  NOTIFICATION_TEMPLATE_DETAILS: '/notificationtemplates/:NotificationTemplateId',
  NOTIFICATION_CONFIGURATION: '/notification-configuration',

  GROUPS: '/groups',
  GROUP_DETAIL: '/groups/:id',

  WORKSPACEACCESS: '/workspaceaccess',

  TAGS: '/tags',
  CREATE_TAGS_TEMPLATE: '/tags/create',
  EDIT_TAGS_TEMPLATE: '/tags/edit',

  FINE_GRAIN_ACCESS_POLICIES: '/fineGrainAccessPolicies',
  VIEW_FINE_GRAIN_ACCESS_POLICY: '/fineGrainAccessPolicies/:id',
  CREATE_FINE_GRAIN_ACCESS_POLICY: '/fineGrainAccessPolicies/create',
  EDIT_FINE_GRAIN_ACCESS_POLICY: '/fineGrainAccessPolicies/edit',

  BASELINING: '/baselining',
  BASELINE_DATA_ACCESS_PERMISSION: '/baseline/data-access-permission/:ownerId',
  BASELINE_HAMMERSTONE_GROUPS: '/baseline/hammerstone/:ownerId',
  BASELINE_OWNER: '/baseline/owner/:ownerId',
  BASELINE_OWNER_WITH_ACCOUNT_ID: '/baseline/owner/:ownerId/accountId/:accountId',
  REVIEWS: '/reviews',
  REVIEW_DETAILS: '/review/:reviewId',
  REVIEW_CUSTODIAN: '/review-custodian',
  REVIEW_HISTORY: '/review-history/:reviewerType',
  LIST_ADVISORIES: '/advisories',
  CREATE_ADVISORY: '/advisories/new',
  CREATE_ADVISORY_FROM_TEMPLATE: '/advisories/new/templateId/:templateid',
  CREATE_ADVISORY_FROM_ADVISORY: '/advisories/new/advisoryId/:id',
  UPDATE_ADVISORY: '/advisories/update/:id',
  ADVISORY_SEND_NOTIFICATION: '/advisories/notify/:id', // need error handling
  LIST_ADVISORY_TEMPLATES: '/advisories/templates',
  UPDATE_ADVISORY_TEMPLATE: '/advisories/templates/update/:id',
  ADVISORY_TEMPLATE_DETAIL: '/advisories/templates/:id',
  ADVISORY_DETAIL: '/advisories/:id',

  // INGESTION
  INGESTION: {
    REGISTER_CONTRACT: '/publisher/register',
    DETAIL: '/publisher/dataset/:arn/:dataset',
    DETAIL_V2: '/publisher/dataset/:arn/:database/:dataset',
    UPDATE_CONTRACT: '/publisher/dataset/update/:arn/:dataset',
    UPDATE_CONTRACT_V2: '/publisher/dataset/update/:arn/:database/:dataset',
    MONITORING: '/publisher/monitoring/:team/:database/:arn/:dataset',
    MONITORING_V2: '/publisher/monitoring/:arn/:database/:dataset',
  },

  // MetricStore
  METRICS_CATALOG: '/metrics-catalog',
  METRIC_DEFINITION: '/metrics-catalog/:id',
  METRIC_COLLECTIONS_CATALOG: '/metric-collections-catalog',
  METRIC_COLLECTIONS_DEFINITION: '/metric-collections-catalog/:id',
  CREATE_METRIC_COLLECTIONS_CATALOG: '',
  REPORTING_TABLE_CATALOG: '/reporting-table-catalog',
  REPORTING_TABLE_DEFINITION: '/reporting-table-catalog/:id',
  METRIC_COLLECTION_MONTHLY_VIEW: '/metric-collections-monthly/:id',
  METRIC_COLLECTION_QUARTERLY_VIEW: '/metric-collections-quarterly/:id',
  METRIC_COLLECTION_WEEKLY_VIEW: '/metric-collections-weekly/:id',
  METRIC_COLLECTION_YEARLY_VIEW: '/metric-collections-yearly/:id',

  // Deprecated paths should still exist for URL backwards compatibility
  // if you change a route you need to keep the old one and redirect to the new route from Routes.tsx
  DEPRECATED_PERMISSIONS: '/permissions',
  DEPRECATED_PERMISSIONS_DETAILS: '/permissions/:id',
  DEPRECATED_CATALOG: '/catalog',
  DEPRECATED_CATALOG_DETAILS: '/catalog/:id',

  CREATE_WORKSPACE: '/workspaces/new',
  BROWSE_WORKSPACES: '/current-workspace',
  WORKSPACE_MY_REQUEST_DETAILS: '/workspaces-requests/:id', // Deprecated
  WORKSPACE_MY_REQUESTS: '/workspaces-requests', // Deprecated
  UPDATE_WORKSPACE: '/workspaces/:workspaceId/update',
  CURRENT_WORKSPACE_DETAIL: '/current-workspace/:workspaceId',

  WORKSPACE_GLUE_REGISTER: '/workspaces-glue-register',
  WORKSPACE_REDSHIFT_REGISTER: '/workspaces-redshift-register',

  WORKSPACE_TAG_REGISTER: '/workspaces-tag-register',

  WORKSPACE_ALL_PERMISSIONS: '/workspaces-permission',

  S3_TABLE_DATABASE_DETAILS: '/databases/:datasourceid/:catalogid/s3/:bucketname/:databasename/:region',
  DATABASE_DETAILS: '/databases/:datasourceid/:catalogid/:databasename/:region',
  DEPRECATED_DATABASE_DETAILS: '/databases/:catalogid/:databasename/:region',
  DEPRECATED_GROUP_DATABASE_DETAILS: '/databases/:catalogid/:databasename',
  S3_TABLE_CATALOG_DETAILS: '/catalogs/:datasourceid/:catalogid/s3/:bucketname/:region',
  CATALOG_DETAILS: '/catalogs/:datasourceid/:catalogid/:region',
  DEPRECATED_LF_CATALOG_DETAILS: '/catalogs/:catalogid/:region',
  DATASET_DETAILS: '/datasets/:id',
  SERVERLESS_SCHEMA_DETAILS:
    '/databases/:datasourceid/:catalogid/serverless/:workgroupname/:databasename/schemas/:schemaname/:region',
  SCHEMA_DETAILS: '/databases/:datasourceid/:catalogid/:clustername/:databasename/schemas/:schemaname/:region',
  DEPRECATED_SCHEMA_DETAILS: '/databases/:catalogid/:clustername/:databasename/schemas/:schemaname/:region',
  REDSHIFT_SERVERLESS_CATALOG_DETAILS: '/catalogs/:datasourceid/:catalogid/serverless/:workgroupname/:region',
  REDSHIFT_CATALOG_DETAILS: '/catalogs/:datasourceid/:catalogid/:clustername/:region',
  DEPRECATED_REDSHIFT_CATALOG_DETAILS: '/catalogs/:catalogid/:clustername/:region',
  REDSHIFT_SERVERLESS_DATABASE_DETAILS:
    '/databases/:datasourceid/:catalogid/serverless/:workgroupname/:databasename/:region',
  REDSHIFT_DATABASE_DETAILS: '/databases/:datasourceid/:catalogid/:clustername/:databasename/:region',
  DEPRECATED_REDSHIFT_DATABASE_DETAILS: '/databases/:catalogid/:clustername/:databasename/:region',

  DEPRECATED_WKS_DATABASE_DETAILS: '/workspaces-databases/:catalogid/:databasename/:region',
  DEPRECATED_WKS_CATALOG_DETAILS: '/workspaces-catalogs/:catalogid/:region',
  DEPRECATED_WKS_DATASET_DETAILS: '/workspaces-datasets/:id',
  DEPRECATED_WKS_SCHEMA_DETAILS:
    '/workspaces-databases/:catalogid/:clustername/:databasename/schemas/:schemaname/:region',
  DEPRECATED_WKS_REDSHIFT_CATALOG_DETAILS: '/workspaces-catalogs/:catalogid/:clustername/:region',
  DEPRECATED_WKS_REDSHIFT_DATABASE_DETAILS: '/workspaces-databases/:catalogid/:clustername/:databasename/:region',

  WORKSPACES_BROWSE_DATASETS: '/workspaces-datasets',
  WORKSPACES_BROWSE_DATABASES: '/workspaces-databases',
  WORKSPACES_BROWSE_CATALOGS: '/workspaces-catalogs',
  WORKSPACE: '/workspace',
  CURRENT_WORKSPACE: '/current-workspace',
  PERMISSION_DETAIL: '/workspace-permissions/:permissionId',

  TEMPLATES: '/templates',
  CREATE_TEMPLATE: '/templates/new',
  EDIT_TEMPLATE: '/templates/edit',
  TEMPLATE_DETAILS: '/templates/:templateId/:resourceId',

  LF_PERMISSION_DETAIL: '/workspaces-permission/lf/:permissionId',
  LF_PERMISSION_DETAIL_SIMPLIFIED: '/lf/:permissionId',
  PUBLISHED_PERMISSION_DETAIL: '/workspaces-permission/published/:permissionId',

  REDSHIFT_PERMISSION_DETAIL: '/workspaces-permission/redshift/:permissionId',
  WORKSPACE_RESOURCE_LINKS: '/workspaces-resource-links',

  WORKSPACE_REDSHIFT_QUERY: '/workspace-redshift-query',

  SEARCH: '/search',

  WORKSPACE_DETAIL: '/search/workspaces/:workspaceId',

  TAG_DETAIL: '/search/tags/:ownerid/:tagid',

  DATAZONE_MANAGE: '/datazone',
  DATAZONE_ONBOARD: '/datazone/onboard',

  WORKFLOW_STATUS: '/workflow/:id',

  TEST_DUA_PAGE: '/testduapage',

  LIST_REPORTS: '/reports',
  START_REPORT_GENERATION: '/reports/new',
  REPORT_DETAIL: '/reports/:id',

  REQUEST_CONTEXT_DETAIL: '/request-context/:id',
  TOOLS_DUA_PLAYGROUND: '/tools/agreement-playground',

  SMUS_RESOURCES: '/smus',
  SMUS_SSO_GROUP_DETAIL: '/smus/sso-group/:groupMetadataId',
};

// Helper functions for generating links

// Resource Manager
export function createResourceGroupLink(id: string) {
  return Page.RESOURCEGROUPS + '/' + id;
}
export function createBootstrapActionLink(id: string) {
  return Page.BOOTSTRAPACTIONS + '/' + id;
}
export function createResourceManagerTemplateLink(id: string) {
  return Page.RG_TEMPLATE_DETAILS.replace(':id', id);
}

// Groups and Workspaces
export function createWorkspaceDetailLink(workspaceId: string) {
  return Page.WORKSPACE_DETAIL.replace(':workspaceId', workspaceId);
}
export function createCurrentWorkspaceDetailLink(workspaceId: string) {
  return Page.CURRENT_WORKSPACE_DETAIL.replace(':workspaceId', workspaceId);
}
export function createUpdateWorkspaceLink(workspaceId: string) {
  return Page.UPDATE_WORKSPACE.replace(':workspaceId', workspaceId);
}
export function createGroupDetailLink(id: string) {
  return Page.GROUP_DETAIL.replace(':id', id);
}
export function createOwnerLink(ownerId: string) {
  return isValidWorkspace(ownerId) ? createWorkspaceDetailLink(ownerId) : createGroupDetailLink(ownerId);
}

// Hybrid Catalog - resource details
export function createCatalogDetailLink(
  dataSourceId: string,
  accountId: string,
  clusterId: string,
  workgroupName: string,
  bucketName: string = undefined,
  region: string,
) {
  // set default data source if none provided
  if (dataSourceId == undefined) {
    dataSourceId = clusterId || workgroupName ? REDSHIFT_DATASOURCE_ID : LAKE_FORMATION_DATASOURCE_ID;
  }
  if (clusterId) {
    return Page.REDSHIFT_CATALOG_DETAILS.replace(':datasourceid', dataSourceId)
      .replace(':catalogid', accountId)
      .replace(':clustername', clusterId)
      .replace(':region', region);
  } else if (workgroupName) {
    return Page.REDSHIFT_SERVERLESS_CATALOG_DETAILS.replace(':datasourceid', dataSourceId)
      .replace(':catalogid', accountId)
      .replace(':workgroupname', workgroupName)
      .replace(':region', region);
  } else if (bucketName) {
    return Page.S3_TABLE_CATALOG_DETAILS.replace(':datasourceid', dataSourceId)
      .replace(':catalogid', accountId)
      .replace(':bucketname', bucketName)
      .replace(':region', region);
  } else {
    return Page.CATALOG_DETAILS.replace(':datasourceid', dataSourceId)
      .replace(':catalogid', accountId)
      .replace(':region', region);
  }
}
export function createDatabaseDetailLink(
  dataSourceId: string,
  accountId: string,
  clusterId: string,
  workgroupName: string,
  bucketName: string = undefined,
  databaseName: string,
  region: string,
) {
  // set default data source if none provided
  if (dataSourceId == undefined) {
    dataSourceId = clusterId || workgroupName ? REDSHIFT_DATASOURCE_ID : LAKE_FORMATION_DATASOURCE_ID;
  }
  if (dataSourceId == REDSHIFT_DATASOURCE_ID || dataSourceId == DZ_REDSHIFT_DATASOURCE_ID) {
    return workgroupName
      ? Page.REDSHIFT_SERVERLESS_DATABASE_DETAILS.replace(':datasourceid', dataSourceId)
          .replace(':catalogid', accountId)
          .replace(':workgroupname', workgroupName)
          .replace(':databasename', databaseName)
          .replace(':region', region)
      : Page.REDSHIFT_DATABASE_DETAILS.replace(':datasourceid', dataSourceId)
          .replace(':catalogid', accountId)
          .replace(':clustername', clusterId)
          .replace(':databasename', databaseName)
          .replace(':region', region);
  } else if (dataSourceId == S3_TABLE_DATASOURCE_ID) {
    return Page.S3_TABLE_DATABASE_DETAILS.replace(':datasourceid', dataSourceId)
      .replace(':catalogid', accountId)
      .replace(':bucketname', bucketName)
      .replace(':databasename', databaseName)
      .replace(':region', region);
  } else {
    return Page.DATABASE_DETAILS.replace(':datasourceid', dataSourceId)
      .replace(':catalogid', accountId)
      .replace(':databasename', databaseName)
      .replace(':region', region);
  }
}
export function createSchemaDetailLink(
  dataSourceId: string,
  catalogId: string,
  clusterIdentifier: string,
  redshiftWorkgroupName: string,
  databaseName: string,
  schemaName: string,
  region: string,
) {
  return redshiftWorkgroupName
    ? Page.SERVERLESS_SCHEMA_DETAILS.replace(':datasourceid', dataSourceId)
        .replace(':catalogid', catalogId)
        .replace(':workgroupname', redshiftWorkgroupName)
        .replace(':databasename', databaseName)
        .replace(':schemaname', schemaName)
        .replace(':region', region)
    : Page.SCHEMA_DETAILS.replace(':datasourceid', dataSourceId)
        .replace(':catalogid', catalogId)
        .replace(':clustername', clusterIdentifier)
        .replace(':databasename', databaseName)
        .replace(':schemaname', schemaName)
        .replace(':region', region);
}
export function createDatasetDetailLink(id: string) {
  return Page.DATASET_DETAILS.replace(':id', id);
}
export function createLinkForResourceArn(arn: string) {
  // determine the type of resource in the ARN and redirect to one of the above helpers
  const idInfo = getIdInfoFromArn(arn);
  const id = generateIdFromArn(arn);
  const scope = getScopeForArn(arn);
  if (scope == Scope.Catalog) {
    return createCatalogDetailLink(
      idInfo.dataSource,
      idInfo.catalogId,
      idInfo.clusterId,
      idInfo.redshiftWorkgroupName,
      idInfo.bucketName,
      idInfo.region,
    );
  } else if (scope == Scope.Database) {
    return createDatabaseDetailLink(
      idInfo.dataSource,
      idInfo.catalogId,
      idInfo.clusterId,
      idInfo.redshiftWorkgroupName,
      idInfo.bucketName,
      idInfo.databaseName,
      idInfo.region,
    );
  } else if (scope == Scope.Schema) {
    return createSchemaDetailLink(
      idInfo.dataSource,
      idInfo.catalogId,
      idInfo.clusterId,
      idInfo.redshiftWorkgroupName,
      idInfo.databaseName,
      idInfo.schemaName,
      idInfo.region,
    );
  } else if (scope == Scope.Table) {
    return createDatasetDetailLink(id);
  } else {
    return Page.HOME;
  }
}

// Hybrid Catalog - event subscriptions
export function createEventSubscriptionDetailsLink(id: string) {
  return Page.EVENT_SUBSCRIPTIONS_DETAILS.replace(':id', id);
}

// Tags
export function createTagDetailLink(ownerId: string, tagId: string) {
  return Page.TAG_DETAIL.replace(':ownerid', ownerId).replace(':tagid', tagId);
}

// Periodic Access Review
export function createBaselineOwnerLink(ownerId: string) {
  return Page.BASELINE_OWNER.replace(':ownerId', ownerId);
}
export const createReviewDetailsLink = (reviewId: string) => {
  return Page.REVIEW_DETAILS.replace(':reviewId', reviewId);
};
export const createReviewHistoryLink = (reviewerType: string) => {
  return Page.REVIEW_HISTORY.replace(':reviewerType', reviewerType);
};

// Data Permissions
export const createWSPermissionLFLink = (permissionId: string) => {
  return Page.LF_PERMISSION_DETAIL.replace(':permissionId', permissionId);
};
export function createWSPermissionRedshiftLink(permissionId: string) {
  return Page.REDSHIFT_PERMISSION_DETAIL.replace(':permissionId', permissionId);
}
export function createWSPermissionPublishedLink(permissionId: string) {
  return Page.PUBLISHED_PERMISSION_DETAIL.replace(':permissionId', permissionId);
}
export function createWSPermissionLink(type: string, permissionId: string) {
  if (type == 'published') {
    return createWSPermissionPublishedLink(permissionId);
  } else if (type == 'lf') {
    return createWSPermissionLFLink(permissionId);
  } else {
    return createWSPermissionRedshiftLink(permissionId);
  }
}
export const createGroupPermissionLFLink = (permissionId: string) => {
  return Page.DATASETSHARE_DETAILS.replace(':id', permissionId);
};

// FGAP
export function createFgaPolicyViewLink(policyId: string) {
  return Page.VIEW_FINE_GRAIN_ACCESS_POLICY.replace(':id', policyId);
}

// Gladstone Templates
export function createTemplateParentDetailsLink(templateId: string) {
  return Page.TEMPLATE_DETAILS.replace(':templateId', templateId).replace(':resourceId', TEMPLATE_RESOURCE_NONE);
}
export function createTemplateResourceDetailsLink(templateId: string, resourceId: string) {
  return Page.TEMPLATE_DETAILS.replace(':templateId', templateId).replace(':resourceId', resourceId);
}

// Advisory
export function createAdvisoryDetailsLink(advisoryId: string) {
  return Page.ADVISORY_DETAIL.replace(':id', advisoryId);
}
export function createAdvisoryTemplateDetailsLink(templateId: string) {
  return Page.ADVISORY_TEMPLATE_DETAIL.replace(':id', templateId);
}
export const createUpdateAdvisoryLink = (advisoryId: string) => {
  return Page.UPDATE_ADVISORY.replace(':id', advisoryId);
};
export const createCreateAdvisoryFromTemplateLink = (templateId: string) => {
  return Page.CREATE_ADVISORY_FROM_TEMPLATE.replace(':templateid', templateId);
};
export const createCreateAdvisoryFromAdvisoryLink = (advisoryId: string) => {
  return Page.CREATE_ADVISORY_FROM_ADVISORY.replace(':id', advisoryId);
};
export const createSendAdvisoryNotificationLink = (advisoryId: string) => {
  return Page.ADVISORY_SEND_NOTIFICATION.replace(':id', advisoryId);
};
export const createUpdateAdvisoryTemplateLink = (templateId: string) => {
  return Page.UPDATE_ADVISORY_TEMPLATE.replace(':id', templateId);
};

// Requests
export const createWorkspaceRequestLink = (requestId: string) => {
  return Page.REQUEST_DETAILS.replace(':id', requestId);
};
export const createRequestDetailsLink = (requestId: string) => {
  return Page.REQUEST_DETAILS.replace(':id', requestId);
};

// Metadata Forms and Business Glossaries
export const createMetadataFormViewLink = (formId: string, ownerId: string) => {
  return Page.VIEW_METADATA_FORM.replace(':id', formId).replace(':formOwnerId', ownerId);
};
export const createGlossaryViewLink = (glossaryId: string, ownerId: string) => {
  return Page.VIEW_BUSINESS_GLOSSARY.replace(':id', glossaryId).replace(':glossaryOwnerId', ownerId);
};
export const createGovernanceTagViewLink = (glossaryId: string, ownerId: string) => {
  return Page.VIEW_GOVERNANCE_TAG.replace(':id', glossaryId).replace(':glossaryOwnerId', ownerId);
};

// External
export function createPosixGroupLink(posixGroup: string) {
  return `https://permissions.amazon.com/group.mhtml?group=${posixGroup}&group_type=posix`;
}

// Metrics
export function createMetricDefinitionLink(id: string) {
  return Page.METRICS_CATALOG + '/' + id;
}
export function createMetricCollectionDefinitionLink(id: string) {
  return Page.METRIC_COLLECTIONS_CATALOG + '/' + id;
}
export function createReportingTableDefinitionLink(id: string) {
  return Page.REPORTING_TABLE_CATALOG + '/' + id;
}
export function createMetricCollectionMonthlyLink(metricCollectionId: string) {
  return Page.METRIC_COLLECTION_MONTHLY_VIEW.replace(':id', metricCollectionId);
}
export function createMetricCollectionWeeklyLink(metricCollectionId: string) {
  return Page.METRIC_COLLECTION_WEEKLY_VIEW.replace(':id', metricCollectionId);
}
export function createMetricCollectionQuarterlyLink(metricCollectionId: string) {
  return Page.METRIC_COLLECTION_QUARTERLY_VIEW.replace(':id', metricCollectionId);
}
export function createMetricCollectionYearlyLink(metricCollectionId: string) {
  return Page.METRIC_COLLECTION_YEARLY_VIEW.replace(':id', metricCollectionId);
}

// DataZone / Gladstone Workflows
export function createWorkflowStatusLink(id: string) {
  return Page.WORKFLOW_STATUS.replace(':id', id);
}

// D3A
export function createReportDetailLink(id: string) {
  return Page.REPORT_DETAIL.replace(':id', id);
}
export function createRequestContextDetailLink(id: string) {
  return Page.REQUEST_CONTEXT_DETAIL.replace(':id', id);
}

// Omni API Notifications
export function createOmniApiNotificationDetailsLink(id: string) {
  return Page.NOTIFICATION_DETAILS.replace(':NotificationId', id);
}
export function createOmniApiBatchNotificationDetailsLink(id: string) {
  return Page.NOTIFICATION_BATCH_DETAILS.replace(':NotificationBatchId', id);
}
export function createOmniApiNotificationTemplateDetailsLink(id: string) {
  return Page.NOTIFICATION_TEMPLATE_DETAILS.replace(':NotificationTemplateId', id);
}

// Tethys
export function createTethysDatasetDetailLink(id: string) {
  return Page.INGESTION.DETAIL.replace(':arn/:dataset', id);
}

// SMUS
export function createSmusSsoGroupDetailLink(groupMetadataId: string) {
  return Page.SMUS_SSO_GROUP_DETAIL.replace(':groupMetadataId', encodeURIComponent(groupMetadataId));
}
